import { useEffect, useState } from "react";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import Term from "@/components/Term";
import CelestialLoader from "@/components/CelestialLoader";
import { PlanetName } from "@/components/PlanetName";
import { useLang } from "@/context/LanguageContext";
import { Map, Compass, BookOpen, Sparkles, Users, Archive, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Journey() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/journey/overview").then(r => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return <CelestialLoader />;

  return (
    <div className="animate-gentle-rise">
      <h1 className="font-display text-[36px] leading-tight" data-testid="journey-title">{t("journey.title")}</h1>
      <p className="text-body mt-2" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("journey.days_walked", { n: d.days_walked }).split(" ").map((w, i) => /^\d/.test(w) ? <KeyData key={i}>{w}</KeyData> : <span key={i}> {w} </span>)}
         · {t("journey.currently_in")} <KeyData>{d.current_chapter.split("–").map((p, i, arr) => <span key={i}><PlanetName planet={p} />{i < arr.length - 1 ? "–" : ""}</span>)}</KeyData>
      </p>

      <div className="ornate-divider" />

      <div className="space-y-4">
        <Card icon={Map} title={<Term k="mahadasha">{t("journey.mahadasha_map")}</Term>} subtitle={t("journey.mahadasha_map_sub")} onClick={() => navigate("/app/journey/mahadasha-map")} testid="journey-mahadasha-map" />
        <Card icon={Compass} title={<Term k="antardasha">{t("journey.antardasha_map")}</Term>} subtitle={t("journey.antardasha_map_sub")} onClick={() => navigate("/app/journey/antardasha-map")} testid="journey-antardasha-map" />
        <Card icon={BookOpen} title={t("journey.journal")} subtitle={t("journey.journal_sub", { n: d.annals.count })} onClick={() => navigate("/app/journey/journal")} testid="journey-journal" />
        <Card icon={Sparkles} title={t("journey.discovery")} subtitle={t("journey.discovery_sub", { a: d.discovery.unlocked, b: d.discovery.total })} onClick={() => navigate("/app/journey/discovery")} testid="journey-discovery" />
        <Card icon={Users} title={t("journey.resonances")} subtitle={t("journey.resonances_sub", { a: d.resonance.unlocked, b: d.resonance.total })} onClick={() => navigate("/app/journey/resonances")} testid="journey-resonances" />
        <Card icon={Archive} title={t("journey.completed")} subtitle={t("journey.completed_sub")} onClick={() => {}} testid="journey-completed" muted />
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, onClick, testid, muted }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      disabled={muted}
      className="vedari-card w-full text-left flex items-center gap-4 transition-all"
      style={{ opacity: muted ? 0.55 : 1 }}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(184,149,74,0.10)" }}>
        <Icon size={22} strokeWidth={1.5} style={{ color: "var(--vedari-gold)" }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif text-h3 mb-1">{title}</h3>
        <p className="text-body-sm" style={{ color: "var(--vedari-ink-soft)" }}>{subtitle}</p>
      </div>
      {!muted && <ChevronRight size={20} style={{ color: "var(--vedari-ink-soft)" }} />}
    </button>
  );
}
